import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    UserFragment,
    Users_Insert_Input,
    UpdateUserMutationVariables,
    User_Roles_Enum,
    User_Type_Enum,
    User_Status_Enum
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    OperationsAdminVenueTalentCreateFragment, 
    OperationsAdminVenueTalentCreateSchema, 
    OperationsAdminVenueTalentUpdateFragment, 
    OperationsAdminVenueTalentUpdateSchema 
} from "src/components/operations/administrator/talent/OperationsAdminVenueTalentSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { uuidToVibrantHexColor } from "src/utils/calendar"

type FormPayloadType = OperationsAdminVenueTalentCreateFragment

let initialState: FormPayloadType = {
    email: "",
    genres: "",
    color: uuidToVibrantHexColor(crypto.randomUUID()),
    timezone: 'America/New_York'
};

export const CreateOperationsAdminVenueTalentDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().users;
    const { loading, errors } = useAppState().users;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Users_Insert_Input) => {
        return createItem({
            role: User_Roles_Enum.AgencyTalentAdministrator,
            type: User_Type_Enum.Talent,
            ...payload
        })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, UserFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueTalentCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateOperationsAdminVenueTalentAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Add Talent'
            action={CreateOperationsAdminVenueTalentDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: OperationsAdminVenueTalentUpdateFragment = {
    status: User_Status_Enum.Active,
    email: "",
    first_name: "",
    last_name: "",
    alias: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    bday: "",
    phone: "",
    website: "",
    instagram: "",
    facebook: "",
    x: "",
    tiktok: "",
    genres: "",
    color: "",
    timezone: 'America/New_York'
};

export const UpdateOperationsAdminVenueTalentDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().users;
    const { currentItem, loading, errors } = useAppState().users;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: OperationsAdminVenueTalentUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<OperationsAdminVenueTalentUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateUserMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<OperationsAdminVenueTalentUpdateFragment, UserFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueTalentUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateOperationsAdminVenueTalentAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().users;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.users.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<UserFragment>
            title='Edit Talent'
            action={UpdateOperationsAdminVenueTalentDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditOperationsAdminVenueTalentAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Edit Talent'
            action={UpdateOperationsAdminVenueTalentDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}